import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Button } from 'react-bootstrap';
import { css } from '@emotion/react';
import { isEmpty } from 'underscore';
import t from 'react-translate';

// redux
import { RootState } from 'redux/schemas';
import { useSelector, useDispatch } from 'react-redux';
import { addAlertMessage } from 'redux/actions/alert-messages';
import { AlertMessageType } from 'redux/schemas/app/alert-message';
import { getFullCourseTimelineContent } from 'redux/actions/timeline';
import { setUpdatingColors, updateColors } from 'redux/actions/lecture-pages';
import { getCurrentLecture, getCurrentLectureSection } from 'redux/selectors/lecture-page';
import { getFlatCourseAliases } from 'redux/selectors/course';

// hooks
import { useLecturePageParams } from 'lecture_pages/hooks/lecture-routing';
import useClickOutside from 'shared/hooks/use-click-outside';

// components
import NvDropdown, { NvDropdownAlign, NvDropdownButtonStyle } from 'shared/components/inputs/nv-dropdown';
import NvColorPickerWithInput from 'shared/components/inputs/nv-color-picker-with-input';
import NvIcon from 'shared/components/nv-icon';
import { getIsColorValueValid, isSameColor } from 'shared/utils';

// styles
import { gray5, primary, white, yellow } from 'styles/global_defaults/colors';
import { dropdownZIndex, halfSpacing } from 'styles/global_defaults/scaffolding';
import { config } from '@config/config.json';

type UpdateColorModalProps = {
  onClose: () => void;
  iconRef: React.RefObject<HTMLDivElement>;
  findColor: string;
  setFindColor: React.Dispatch<React.SetStateAction<string>>;
  replaceColor: string;
  setReplaceColor: React.Dispatch<React.SetStateAction<string>>;
};

export const UpdateColorModal = ({
  onClose,
  iconRef,
  findColor,
  setFindColor,
  replaceColor,
  setReplaceColor,
}: UpdateColorModalProps) => {
  const styles = css`
    .modal-container {
      top: 100%;
      left: 0;
      width: 500px;
      background: ${white};
      border: 1px solid ${gray5};
      border-radius: ${halfSpacing}px;
      box-shadow: 0px 10px 10px 0px #1D21261A;
      z-index: ${dropdownZIndex};
    }

    .color-dropdown-select {
      margin-bottom: 25px;
    }

    .cancel-button {
      &:hover {
        color: ${primary};
        background-color: ${white} !important;
      }

      &:active , &:focus {
        box-shadow: none !important;
      }

      &:active {
        color: ${primary} !important;
        background-color: ${white} !important;
      }
    }
  `;

  const dispatch = useDispatch();
  const modalRef = useRef<HTMLDivElement>(null);

  const [activePicker, setActivePicker] = useState<null | 'findColor' | 'replaceColor'>(null);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);

  // Redux Data
  const params = useLecturePageParams();
  const currentLecture = useSelector(getCurrentLecture);
  const currentSubSection = useSelector((state: RootState) => getCurrentLectureSection(state, 'LectureSubsection'));
  const currentSection = useSelector((state: RootState) => getCurrentLectureSection(state, 'LectureSection'));
  const courseAliases = useSelector((state: RootState) => getFlatCourseAliases(state, params.catalogId));
  const catalogId = useSelector((state: RootState) => state.app.currentCatalogId);

  const isUpdateButtonDisabled = !getIsColorValueValid(findColor)
    || !getIsColorValueValid(replaceColor)
    || isSameColor(findColor, replaceColor);

  // Fetch timeline content only once when modal opens
  useEffect(() => {
    if (!currentLecture || (!currentSection && !currentSubSection)) {
      dispatch(getFullCourseTimelineContent({ catalogId: params.catalogId }));
    }
  }, [currentLecture, currentSection, currentSubSection, params.catalogId, dispatch]);

  // Handle outside click
  useClickOutside(modalRef, (e) => {
    if (iconRef.current && iconRef.current.contains(e.target as any)) return;
    onClose();
  }, []);

  // Dropdown options
  const contentOptions = [];

  if (!isEmpty(currentLecture)) {
    contentOptions.push({
      id: currentLecture.id,
      textOnSelected: (
        <div className='text-regular dropdown-item-ellipsis'>
          {t.LECTURE_PAGES.COMPONENTS.UPDATE_COURSE_COLOR.DROPDOWN_SECTION.CURRENT_LESSON(courseAliases)} (<strong>{currentLecture.title}</strong>)
        </div>
      ),
      text: t.LECTURE_PAGES.COMPONENTS.UPDATE_COURSE_COLOR.DROPDOWN_SECTION.CURRENT_LESSON(courseAliases),
    });
  }

  if (!isEmpty(currentSubSection)) {
    contentOptions.push({
      id: currentSubSection.id,
      textOnSelected: (
        <div className='text-regular dropdown-item-ellipsis'>
          {t.LECTURE_PAGES.COMPONENTS.UPDATE_COURSE_COLOR.DROPDOWN_SECTION.CURRENT_SUB_SECTION()} (<strong>{currentSubSection.title}</strong>)
        </div>
      ),
      text: t.LECTURE_PAGES.COMPONENTS.UPDATE_COURSE_COLOR.DROPDOWN_SECTION.CURRENT_SUB_SECTION(),
    });
  }

  if (!isEmpty(currentSection)) {
    contentOptions.push({
      id: currentSection.id,
      textOnSelected: (
        <div className='text-regular dropdown-item-ellipsis'>
          {t.LECTURE_PAGES.COMPONENTS.UPDATE_COURSE_COLOR.DROPDOWN_SECTION.CURRENT_SECTION()} (<strong>{currentSection.title}</strong>)
        </div>
      ),
      text: t.LECTURE_PAGES.COMPONENTS.UPDATE_COURSE_COLOR.DROPDOWN_SECTION.CURRENT_SECTION(),
    });
  }

  contentOptions.push({
    id: 'all-lessons',
    textOnSelected: t.LECTURE_PAGES.COMPONENTS.UPDATE_COURSE_COLOR.DROPDOWN_SECTION.ALL_LESSONS(courseAliases),
    text: t.LECTURE_PAGES.COMPONENTS.UPDATE_COURSE_COLOR.DROPDOWN_SECTION.ALL_LESSONS(courseAliases),
  });

  const dropdownItems = useMemo(() => contentOptions.map((option, idx) => ({
    id: String(option.id ?? idx),
    type: 'text' as const,
    text: option.text,
    textOnSelected: option.textOnSelected,
    callback: () => {
      setSelectedIndex(idx);
      setIsDropdownOpen(false);
    },
  })), [contentOptions]);

  // Handle Update
  const handleUpdateColorButton = () => {
    onClose();
    dispatch(setUpdatingColors(true));
    dispatch(addAlertMessage({
      type: AlertMessageType.INFO,
      header: t.LECTURE_PAGES.COMPONENTS.UPDATE_COURSE_COLOR.ALERT_MESSAGES.ALERT_INFO_HEADER(),
      message: t.LECTURE_PAGES.COMPONENTS.UPDATE_COURSE_COLOR.ALERT_MESSAGES.ALERT_INFO_DESCRIPTION(),
      customColor: `${yellow}`,
    }));

    // Base payload
    const payload: any = {
      catalogId,
      colorToReplace: findColor,
      newColor: replaceColor,
    };

    // Add lessonId/sectionId only if selected
    const selected = contentOptions[selectedIndex];
    if (selected?.id && selected.id !== 'all-lessons') {
      if (selected.id === currentLecture?.id) {
        payload.lessonId = currentLecture.id;
      } else if (selected.id === currentSection?.id) {
        payload.sectionId = currentSection.id;
      } else if (selected.id === currentSubSection?.id) {
        payload.sectionId = currentSubSection.id;
      }
    }

    dispatch(updateColors(payload));
  };

  return (
    <div css={styles}>
      <div ref={modalRef} className='modal-container position-absolute p-6'>
        <div className='header page-title-small text-align-center'>{t.LECTURE_PAGES.COMPONENTS.UPDATE_COURSE_COLOR.UPDATE_COLOR_FOR()}</div>

        <div className='color-dropdown-select mt-2'>
          <NvDropdown
            items={dropdownItems}
            initialIndex={selectedIndex >= 0 ? selectedIndex : 0}
            buttonStyle={NvDropdownButtonStyle.CONDENSED_TEXT}
            align={NvDropdownAlign.CENTER}
            show={isDropdownOpen}
            onToggle={(isOpen) => setIsDropdownOpen(isOpen)}
            ariaExpanded={isDropdownOpen}
          />
        </div>

        <div className='align-items-center justify-content-between mb-8 d-flex'>
          <div>
            <div className='text-small font-weight-bold text-gray-3'>{t.LECTURE_PAGES.COMPONENTS.UPDATE_COURSE_COLOR.FIND_THIS_COLOR()}</div>
            <NvColorPickerWithInput<{
              textColor: string;
            }>
              sections={[{ name: 'textColor', title: 'Text Color' }]}
              colorValues={{ textColor: findColor }}
              onChange={(key, newColor) => setFindColor(newColor)}
              placeholder={t.LECTURE_PAGES.COMPONENTS.UPDATE_COURSE_COLOR.PLACEHOLDER_VALUE()}
              placement='right-end'
              container={modalRef.current}
              show={activePicker === 'findColor'}
              onToggle={(toggleValue) => {
                if (toggleValue) {
                  setActivePicker('findColor');
                } else if (activePicker === 'findColor') {
                  setActivePicker(null);
                }
              }}
            />
          </div>
          <div className='color-arrow-wrapper pt-2'>
            <NvIcon icon='expand' size='small' />
          </div>
          <div>
            <div className='text-small font-weight-bold text-gray-3'>{t.LECTURE_PAGES.COMPONENTS.UPDATE_COURSE_COLOR.REPLACE_WITH_NEW_COLOR()}</div>
            <NvColorPickerWithInput<{
              textColor: string;
            }>
              sections={[{ name: 'textColor', title: 'Text Color' }]}
              colorValues={{ textColor: replaceColor }}
              onChange={(key, newColor) => setReplaceColor(newColor)}
              placeholder={t.LECTURE_PAGES.COMPONENTS.UPDATE_COURSE_COLOR.PLACEHOLDER_VALUE()}
              placement='right-end'
              container={modalRef.current}
              show={activePicker === 'replaceColor'}
              onToggle={(toggleValue) => {
                if (toggleValue) {
                  setActivePicker('replaceColor');
                } else if (activePicker === 'replaceColor') {
                  setActivePicker(null); // close only if this one is open
                }
              }}
            />
          </div>
        </div>

        <div className='d-flex justify-content-center'>
          <Button
            className='cancel-button font-weight-bold'
            variant='outline-primary'
            onClick={onClose}
            data-qa={config.pendo.updateColors.buttons.cancelButton}
          >
            {t.FORM.CANCEL()}
          </Button>
          <Button
            className='font-weight-bold ml-2'
            variant='primary'
            disabled={isUpdateButtonDisabled}
            onClick={handleUpdateColorButton}
            data-qa={config.pendo.updateColors.buttons.updateColorsButton}
          >
            {t.LECTURE_PAGES.COMPONENTS.UPDATE_COURSE_COLOR.UPDATE_COLORS_BUTTON()}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default UpdateColorModal;
